import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Tooltip } from '@mui/material';
import Add from '@mui/icons-material/Add';

import { getVideoDuration } from '../../helpers';
import { navIcon, navItem, navLabel } from '../../theme';
import styles from './style';

interface AddVideoButtonProps {
  // "Ajoute ta vidéo" by default (user), "Ajouter une vidéo" for the admin.
  label?: string;
  // A valid file was picked (60 s and 200 Mo max).
  onPicked: (file: File) => void;
  onError: (message: string) => void;
  // Takes the width of its column (two buttons of the same size side by side).
  fullWidth?: boolean;
  // A discreet round "+" next to a page title, the label in its tooltip.
  round?: boolean;
  // An item of the side menu ("Publier").
  nav?: boolean;
}

const MB = 1024 * 1024;
const MAX_SIZE = 200 * MB;
const MAX_DURATION = 60;

const AddVideoButton: React.FC<AddVideoButtonProps> = React.memo((props) => {
  const {
    label,
    onPicked,
    onError,
    fullWidth,
    nav,
    round,
  } = props;
  const { t } = useTranslation();
  const [value, setValue] = useState('');

  // Checked as soon as the file is picked, before any upload: the size first (it needs no reading),
  // then the duration, from the metadata only.
  const onChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    setValue('');
    if (!files || !files.length) {
      return;
    }
    const file = files[0];
    if (file.size > MAX_SIZE) {
      onError(t('admin.upload.tooBig', { size: Math.ceil(file.size / MB) }));
      return;
    }
    let duration = 0;
    try {
      duration = await getVideoDuration(file);
    } catch (err) {
      onError(t('admin.upload.unreadable'));
      return;
    }
    if (!Number.isFinite(duration)) {
      onError(t('admin.upload.unreadable'));
    } else if (duration > MAX_DURATION) {
      onError(t('admin.upload.tooLong', { duration: Math.ceil(duration) }));
    } else {
      onPicked(file);
    }
  }, [onError, onPicked, t]);

  let buttonLabel = t('admin.myVideos.addVideo');
  if (label) {
    buttonLabel = label;
  }

  if (nav) {
    return (
      <Box component="label" sx={navItem}>
        <input
          value={value}
          onChange={onChange}
          accept="video/*"
          multiple={false}
          style={styles.input}
          type="file"
        />
        <Box sx={navIcon}><Add /></Box>
        <Box component="span" sx={navLabel}>{buttonLabel}</Box>
      </Box>
    );
  }

  if (round) {
    return (
      <Tooltip title={buttonLabel}>
        <Box component="label" aria-label={buttonLabel} sx={styles.label}>
          <input
            value={value}
            onChange={onChange}
            accept="video/*"
            multiple={false}
            style={styles.input}
            type="file"
          />
          <Box component="span" sx={styles.round}>
            <Add sx={styles.icon} />
          </Box>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box component="label" sx={fullWidth ? styles.labelFull : styles.label}>
      <input
        value={value}
        onChange={onChange}
        accept="video/*"
        multiple={false}
        style={styles.input}
        type="file"
      />
      <Button component="span" sx={fullWidth ? styles.buttonFull : styles.button}>
        <Add sx={styles.icon} />
        {buttonLabel}
      </Button>
    </Box>
  );
});

export default AddVideoButton;
